import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Plus } from "lucide-react";
import { ROUTES } from "../../../routes";
import { PaymentType } from "../../payment-types/models/PaymentType";
import { formatMoney } from "../../billing/utils/money";
import { LateCancellationOutcome } from "../enums/LateCancellationOutcome";
import { CancellationPolicy } from "../models/CancellationPolicy";

/** The editable values of a cancellation policy. */
export interface CancellationPolicyValues {
  cutoffHours: number;
  lateOutcome: LateCancellationOutcome;
  feePaymentTypeId: string | null;
  note: string;
}

export const toPolicyValues = (policy: CancellationPolicy): CancellationPolicyValues => ({
  cutoffHours: policy.cutoffHours,
  lateOutcome: policy.lateOutcome,
  feePaymentTypeId: policy.fee?.paymentTypeId ?? null,
  note: policy.note ?? "",
});

interface CancellationPolicyFieldsProps {
  idPrefix: string;
  values: CancellationPolicyValues;
  paymentTypes: PaymentType[];
  disabled?: boolean;
  onChange: (values: CancellationPolicyValues) => void;
}

const OUTCOMES: Array<{ value: LateCancellationOutcome; key: string }> = [
  { value: LateCancellationOutcome.ChargedFull, key: "settings.cancellationPolicy.outcomeFull" },
  { value: LateCancellationOutcome.ChargedFee, key: "settings.cancellationPolicy.outcomeFee" },
  { value: LateCancellationOutcome.Waived, key: "settings.cancellationPolicy.outcomeWaived" },
];

/** Cutoff hours, late outcome, fee Payment Type and note — shared by the settings card and the Session form. */
const CancellationPolicyFields = ({ idPrefix, values, paymentTypes, disabled = false, onChange }: CancellationPolicyFieldsProps) => {
  const { t, i18n } = useTranslation();
  const set = (change: Partial<CancellationPolicyValues>) => onChange({ ...values, ...change });

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <div>
        <label htmlFor={`${idPrefix}_cutoff`} className="mb-1 block text-sm font-medium text-grey-30 dark:text-white">
          {t("settings.cancellationPolicy.cutoffLabel")}
        </label>
        <input
          id={`${idPrefix}_cutoff`}
          type="number"
          min={0}
          max={168}
          className="input w-full sm:w-40"
          value={values.cutoffHours}
          disabled={disabled}
          onChange={(e) => set({ cutoffHours: Math.max(0, Math.min(168, Number(e.target.value) || 0)) })}
        />
        <p className="mt-1 text-xs text-grey-50 dark:text-grey-70">{t("settings.cancellationPolicy.cutoffHint")}</p>
      </div>

      <fieldset disabled={disabled}>
        <legend className="mb-1 block text-sm font-medium text-grey-30 dark:text-white">
          {t("settings.cancellationPolicy.outcomeLabel")}
        </legend>
        <div className="flex flex-col gap-1">
          {OUTCOMES.map((outcome) => (
            <label key={outcome.value} className="flex items-center gap-2 text-sm text-grey-20 dark:text-white">
              <input
                type="radio"
                name={`${idPrefix}_outcome`}
                value={outcome.value}
                checked={values.lateOutcome === outcome.value}
                onChange={() => set({ lateOutcome: outcome.value })}
              />
              {t(outcome.key)}
            </label>
          ))}
        </div>
      </fieldset>

      {values.lateOutcome === LateCancellationOutcome.ChargedFee && (
        <div>
          <label htmlFor={`${idPrefix}_fee`} className="mb-1 block text-sm font-medium text-grey-30 dark:text-white">
            {t("settings.cancellationPolicy.feeLabel")}
          </label>
          <select
            id={`${idPrefix}_fee`}
            className="input w-full"
            value={values.feePaymentTypeId ?? ""}
            disabled={disabled}
            onChange={(e) => set({ feePaymentTypeId: e.target.value || null })}
          >
            <option value="">{t("settings.cancellationPolicy.feePlaceholder")}</option>
            {paymentTypes.map((type) => (
              <option key={type.id} value={type.id}>
                {type.name} ({formatMoney(type.priceAmountMinorUnits, type.currency, i18n.language)})
              </option>
            ))}
          </select>
          <Link
            to={`${ROUTES.BILLING}?tab=payment-types`}
            className="mt-1 inline-flex items-center gap-1 text-sm text-primary-40 hover:underline dark:text-primary-90"
          >
            <Plus size={14} />
            {t("settings.cancellationPolicy.newPaymentType")}
          </Link>
        </div>
      )}

      <div className="lg:col-span-2">
        <label htmlFor={`${idPrefix}_note`} className="mb-1 block text-sm font-medium text-grey-30 dark:text-white">
          {t("settings.cancellationPolicy.noteLabel")}
        </label>
        <textarea
          id={`${idPrefix}_note`}
          className="input w-full"
          rows={2}
          maxLength={500}
          value={values.note}
          disabled={disabled}
          onChange={(e) => set({ note: e.target.value })}
        />
        <p className="mt-1 text-xs text-grey-50 dark:text-grey-70">{t("settings.cancellationPolicy.noteHint")}</p>
      </div>
    </div>
  );
};

export default CancellationPolicyFields;
